import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Send, ShieldCheck, X } from 'lucide-react';
import { api, type OrganizationRole, type SubmitRoleRequestInput } from '../lib/api';

// ─────────────────────────────────────────────────────────────────────────────
// RoleRequestForm — modal for submitting an org role-request (Phase 4).
//
// The user picks an elevated role they want within the org they're already a
// member of. Allowed transitions:
//   ORG_MEMBER  → ORG_ADMIN
//   ORG_ADMIN   → ORG_OWNER  (founder-only at approve time)
//
// COMP SCOPE RETIRED (2026-08-02): the ORG_MEMBER → COMP_ADMIN row and its
// comp picker are gone. The role spine folded every comp capability into
// org.* at ADMIN (src/shared/roleSpine.ts) and the spine backfill auto-denies
// PENDING COMP_ADMIN requests, so this option asked people to wait on a
// decision that had already been taken against them. ORG_ADMIN is the live
// route to the same job.
//
// The server is the source of truth; we compute the option list client-side
// purely for UX. 429 responses (10-pending cap, 24h cooldown) carry
// JSON-stringified payloads in the error message — we surface them verbatim.
// ─────────────────────────────────────────────────────────────────────────────

export interface RoleRequestFormProps {
  open: boolean;
  onClose: () => void;
  orgId: string;
  orgName?: string;
  currentOrgRole: OrganizationRole;
}

type RequestableRoleOption =
  | 'ORG_ADMIN'
  | 'ORG_OWNER';

function allowedRolesFor(currentRole: OrganizationRole): RequestableRoleOption[] {
  switch (currentRole) {
    case 'ORG_MEMBER':
      return ['ORG_ADMIN'];
    case 'ORG_ADMIN':
      return ['ORG_OWNER'];
    case 'ORG_OWNER':
    case 'ORG_VIEWER':
    default:
      return [];
  }
}

function roleLabel(role: RequestableRoleOption): string {
  switch (role) {
    case 'ORG_ADMIN':  return 'Org Admin (org-wide)';
    case 'ORG_OWNER':  return 'Org Owner (founder review required)';
  }
}

interface ParsedRateLimit {
  kind: 'pending_limit' | 'cooldown' | 'mismatch' | 'transition' | 'unknown';
  message: string;
  pendingCount?: number;
  pendingLimit?: number;
  cooldownRemainingMs?: number;
  actualCurrentRole?: string;
}

function parseRateLimitError(err: unknown): ParsedRateLimit {
  const raw = err instanceof Error ? err.message : String(err);
  // The server stuffs structured payloads into the `error` field as JSON
  // strings for 429 / current-role-mismatch / transition-not-allowed cases.
  // apiFetch appends " (HTTP <status>)" to the thrown message, which makes
  // the raw string invalid JSON on its own — strip it before parsing (same
  // fix as parseApiError in ../lib/api.ts) or every real call falls through
  // to the catch below and shows the raw blob.
  const stripped = raw.replace(/\s\(HTTP \d+\)$/, '');
  try {
    const parsed = JSON.parse(stripped);
    if (parsed && typeof parsed === 'object') {
      const obj = parsed as Record<string, unknown>;
      const message = typeof obj.message === 'string' ? obj.message : stripped;
      const kind = obj.error;
      if (kind === 'pending_limit_reached') {
        return {
          kind: 'pending_limit',
          message,
          pendingCount: Number(obj.pendingCount) || undefined,
          pendingLimit: Number(obj.pendingLimit) || undefined,
        };
      }
      if (kind === 'cooldown') {
        return {
          kind: 'cooldown',
          message,
          cooldownRemainingMs: Number(obj.cooldownRemainingMs) || undefined,
        };
      }
      if (kind === 'current_role_mismatch') {
        return {
          kind: 'mismatch',
          message,
          actualCurrentRole: typeof obj.actualCurrentRole === 'string' ? obj.actualCurrentRole : undefined,
        };
      }
      if (kind === 'transition_not_allowed') {
        return { kind: 'transition', message };
      }
      return { kind: 'unknown', message };
    }
  } catch {
    // Not JSON — fall through.
  }
  return { kind: 'unknown', message: stripped };
}

export function RoleRequestForm({
  open,
  onClose,
  orgId,
  orgName,
  currentOrgRole,
}: RoleRequestFormProps) {
  const queryClient = useQueryClient();

  const allowedRoles = useMemo(() => allowedRolesFor(currentOrgRole), [currentOrgRole]);
  const [requestedRole, setRequestedRole] = useState<RequestableRoleOption | ''>('');
  const [reason, setReason] = useState('');
  const [submitError, setSubmitError] = useState<ParsedRateLimit | null>(null);

  // Reset state when the modal is opened or the org changes.
  useEffect(() => {
    if (open) {
      setRequestedRole('');
      setReason('');
      setSubmitError(null);
    }
  }, [open, orgId]);

  const submitMut = useMutation({
    mutationFn: (input: SubmitRoleRequestInput) => api.submitRoleRequest(orgId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myRoleRequests'] });
      onClose();
    },
    onError: (e: Error) => setSubmitError(parseRateLimitError(e)),
  });

  if (!open) return null;

  const canSubmit = !!requestedRole && !!reason.trim() && reason.length <= 500;

  const onSubmit = () => {
    if (!requestedRole) return;
    setSubmitError(null);
    const input: SubmitRoleRequestInput = {
      currentRole: currentOrgRole,
      requestedRole,
      reason: reason.trim(),
    };
    submitMut.mutate(input);
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm"
        onClick={() => !submitMut.isPending && onClose()}
      />
      <div className="fixed inset-x-0 bottom-0 z-[80] border-t border-forge-500/40 bg-iron-950 p-5 shadow-forged sm:inset-x-auto sm:bottom-auto sm:right-6 sm:top-1/2 sm:w-full sm:max-w-md sm:-translate-y-1/2 sm:border">
        <div className="mb-4 flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center border border-forge-500/40 bg-forge-500/10">
            <ShieldCheck className="h-4 w-4 text-forge-300" strokeWidth={2.5} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-display text-sm font-black uppercase tracking-wide text-white">
              Request elevated role
            </p>
            <p className="mt-1 truncate text-xs text-iron-400">
              {orgName ?? orgId.replace(/^ORG#/, '')} · current role <span className="font-mono">{currentOrgRole}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitMut.isPending}
            className="flex h-7 w-7 shrink-0 items-center justify-center border border-iron-800 text-iron-500 transition-colors hover:border-iron-600 hover:text-iron-300 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-3 w-3" strokeWidth={2.5} />
          </button>
        </div>

        {allowedRoles.length === 0 ? (
          <p className="rounded border border-iron-800 bg-iron-950 px-3 py-2 text-xs text-iron-400">
            No role elevations are available from <span className="font-mono">{currentOrgRole}</span>.
            {currentOrgRole === 'ORG_OWNER' && ' You are already at the top of the org hierarchy.'}
            {currentOrgRole === 'ORG_VIEWER' && ' Ask the org owner to upgrade you to ORG_MEMBER first.'}
          </p>
        ) : (
          <div className="space-y-4">
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] font-bold uppercase tracking-wider text-iron-400">Requested role</span>
              <select
                className="input w-full"
                value={requestedRole}
                onChange={e => setRequestedRole(e.target.value as RequestableRoleOption | '')}
              >
                <option value="">Select a role…</option>
                {allowedRoles.map(r => (
                  <option key={r} value={r}>{roleLabel(r)}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block font-mono text-[10px] font-bold uppercase tracking-wider text-iron-400">
                Reason ({reason.length}/500)
              </span>
              <textarea
                className="input w-full resize-none"
                rows={4}
                maxLength={500}
                placeholder="Why do you want this role?"
                value={reason}
                onChange={e => setReason(e.target.value)}
              />
            </label>

            {submitError && (
              <div className="flex items-start gap-2 rounded border border-red-500/40 bg-red-950/40 px-3 py-2 text-xs text-red-200">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <div className="space-y-1">
                  <p>{submitError.message}</p>
                  {submitError.kind === 'pending_limit' && submitError.pendingCount != null && submitError.pendingLimit != null && (
                    <p className="font-mono text-[10px] text-red-300/80">
                      pending {submitError.pendingCount}/{submitError.pendingLimit}
                    </p>
                  )}
                  {submitError.kind === 'cooldown' && submitError.cooldownRemainingMs != null && (
                    <p className="font-mono text-[10px] text-red-300/80">
                      retry in ~{Math.ceil(submitError.cooldownRemainingMs / 3600000)}h
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={submitMut.isPending}
                className="flex-1 border border-iron-700 bg-iron-950 px-3 py-2 font-display text-[11px] font-black uppercase tracking-wider text-iron-200 transition-colors hover:border-iron-500 hover:bg-iron-900 hover:text-white disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onSubmit}
                disabled={!canSubmit || submitMut.isPending}
                className="ember flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 font-display text-[11px] font-black uppercase tracking-wider disabled:opacity-50"
              >
                <Send className="h-3.5 w-3.5" strokeWidth={2.5} />
                {submitMut.isPending ? 'Submitting…' : 'Submit request'}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
